import { useEffect, useState } from "react"
import { Input, Button, message, Row, Col, Typography, Dropdown, Tag } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import { useNavigate, useParams } from "react-router-dom"
import "../AppManagement.scss"

const { Text } = Typography

const AppBundlesForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = !!id
  const title = isEditMode ? "Edit app bundle" : "Add app bundle"

  const [selectedApps, setSelectedApps] = useState([])
  const [groupName, setGroupName] = useState("")
  const [searchAppName, setSearchAppName] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isEditMode) getGroupData(id)
  }, [id])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchAppName.trim()) findAppName()
      else {
        setSuggestions([])
        setShowSuggestions(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchAppName])

  const findAppName = async () => {
    try {
      const res = await apiClient(`choco-repo/search?q=${searchAppName}`, "GET")
      if (res?.result) {
        setSuggestions(res.result)
        setShowSuggestions(true)
      }
    } catch (err) {
      console.log("Error while searching app:", err)
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  const getGroupData = async (groupId) => {
    try {
      const res = await apiClient(`group-apps/get-ById/${groupId}`)
      if (res?.success) {
        setGroupName(res.result.name)
        setSelectedApps(res.result.applicationsList || [])
      }
    } catch (err) {
      message.error("Failed to fetch group data")
    }
  }

  const handleAppSelect = (app) => {
    const isAlreadySelected = selectedApps.some(
      (selectedApp) =>
        selectedApp.PackageId === app.PackageId || selectedApp === app.PackageId
    )
    if (!isAlreadySelected) {
      setSelectedApps((prev) => [...prev, app])
      message.success(`${app.PackageId} added`)
    } else {
      message.info(`${app.PackageId} is already in this bundle`)
    }
    setSearchAppName("")
    setShowSuggestions(false)
    setSuggestions([])
  }

  const handleRemoveApp = (appId) => {
    setSelectedApps((prev) => prev.filter((app) => app.PackageId !== appId))
  }

  const handleSubmit = async () => {
    if (!groupName.trim()) {
      message.error("Please enter a bundle name")
      return
    }
    if (selectedApps.length === 0) {
      message.error("Please select at least one application")
      return
    }

    const payload = {
      groupName,
      applications: selectedApps.map((app) => ({
        PackageId: app.PackageId,
        iconUrl: app.iconUrl,
        DownloadCount: app.DownloadCount,
        version: app.version,
      })),
    }

    setSaving(true)
    try {
      const endpoint = isEditMode
        ? `group-apps/update/${id}`
        : "group-apps/create"
      const method = isEditMode ? "PUT" : "POST"
      const res = await apiClient(endpoint, method, payload)
      if (res?.success) {
        message.success(res.message)
        navigate("/app-bundles")
      } else {
        message.error(res?.message || "Operation failed")
      }
    } catch (err) {
      message.error("Error occurred")
    } finally {
      setSaving(false)
    }
  }

  return (
    <PanelLayout
      title={title}
      subtitle="Choose the apps that belong in this deployable bundle."
      actions={
        <>
          <Button onClick={() => navigate("/app-bundles")}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            {isEditMode ? "Save bundle" : "Create bundle"}
          </Button>
        </>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <label className="app-mgmt__kpi-label" style={{ marginBottom: 6, display: "block" }}>
            Bundle name
          </label>
          <Input
            placeholder="e.g. Finance baseline"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </Col>
        <Col xs={24} md={12}>
          <label className="app-mgmt__kpi-label" style={{ marginBottom: 6, display: "block" }}>
            Add applications
          </label>
          <Dropdown
            open={showSuggestions && suggestions.length > 0}
            menu={{
              items: suggestions.map((item, index) => ({
                key: `${item.PackageId}-${index}`,
                label: (
                  <div>
                    <div className="app-mgmt__name">{item.PackageId}</div>
                    {item.Title ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.Title}
                      </Text>
                    ) : null}
                  </div>
                ),
                onClick: () => handleAppSelect(item),
              })),
            }}
            placement="bottomLeft"
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search Chocolatey catalog"
              value={searchAppName}
              onChange={(e) => setSearchAppName(e.target.value)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true)
              }}
            />
          </Dropdown>
        </Col>
      </Row>

      <div className="app-mgmt__section" style={{ marginTop: 20 }}>
        <h2>Selected applications ({selectedApps.length})</h2>
        {selectedApps.length > 0 ? (
          <div className="app-mgmt__chips">
            {selectedApps.map((app) => (
              <Tag
                key={app.PackageId}
                closable
                onClose={() => handleRemoveApp(app.PackageId)}
              >
                <span className="app-mgmt__app">
                  {app.iconUrl ? (
                    <img src={app.iconUrl} alt="" className="app-mgmt__icon" />
                  ) : null}
                  {app.PackageId}
                </span>
              </Tag>
            ))}
          </div>
        ) : (
          <Text type="secondary">Search the catalog and add apps to this bundle.</Text>
        )}
      </div>
    </PanelLayout>
  )
}

export default AppBundlesForm
